import React from 'react';
import clsx from 'clsx';
import { ShipmentLabelTypeEnum, ShippingLabelCarrier, ShippingLabelType } from '@interfaces/models/timeline';
import Icon from '@components/atoms/icon/icon';
import { useTranslation } from 'next-i18next';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import RadioButton from '@components/atoms/radio-button/radio-button';
import styles from './select-carrier-options.module.scss';

const LANG_PREFIX = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';

interface LabelTypeOptionProps {
  handleSelectCarrier: (carrier: ShippingLabelCarrier) => void;
  carrier: ShippingLabelCarrier;
}

const LabelTypeOptions = ({ handleSelectCarrier, carrier }: LabelTypeOptionProps) => {
  const { t } = useTranslation('orders-sales');

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { shipmentLabelTypes = [] } = carrier || {};

  if (shipmentLabelTypes.length === 0) {
    return null;
  }

  const onTypeChange = (labelType: ShippingLabelType) => {
    carrier.shipmentLabelTypes = shipmentLabelTypes.map((s) => ({ ...s, selected: s.type === labelType.type }));
    handleSelectCarrier({ ...carrier });
    sendAnalyticEvent('timeline_select_carrier_and_options_preference_selection', { label: labelType.type });
  };

  return (
    <>
      <p>{t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.LABEL_PREFERENCE')}</p>

      {shipmentLabelTypes.map((preference) => {
        const { type, selected } = preference;
        const isShippingCode = type === ShipmentLabelTypeEnum.shippingCode;

        return (
          <div
            key={type}
            className={clsx(styles.radioItem, {
              [styles.radioItemSelected]: selected,
            })}
          >
            <RadioButton
              className={clsx(styles.radioItem_button, {
                [styles.radioItem_buttonClosed]: !selected,
              })}
              position="left"
              id={`label-type-${carrier.name}-${type}`}
              name={`label-type-${carrier.name}-${type}`}
              value={type}
              checked={selected}
              onChange={() => onTypeChange(preference)}
              label={
                <div className={styles.radioItem_header}>
                  <p className={clsx({ [styles.radioItem_button_boldLabel]: selected })}>
                    {isShippingCode
                      ? t(`${LANG_PREFIX}LABEL_PREFERENCE.DIGITAL`)
                      : t(`${LANG_PREFIX}LABEL_PREFERENCE.PRINTABLE`)}
                  </p>

                  {isShippingCode && (
                    <div className={styles.radioItem_header_icon}>
                      <Icon
                        name="leaf-outline-default"
                        width={18}
                        height={18}
                        ariaHidden
                      />
                      {t(`${LANG_PREFIX}LABEL_PREFERENCE.PAPERLESS`)}
                    </div>
                  )}
                </div>
              }
            />

            {selected && (
              <p>
                {isShippingCode
                  ? t(`MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.LABEL_PREFERENCE.DIGITAL_DESC`)
                  : t(`MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.LABEL_PREFERENCE.PRINTABLE_DESC`)}
              </p>
            )}
          </div>
        );
      })}
    </>
  );
};

export default LabelTypeOptions;
